import { Box, Card, Grid, IconButton, LinearProgress, Typography } from '@mui/material'
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import moment from 'moment'
import { useState } from 'react'
import StaffingRequestDetails from '../StaffingRequestDetails'

const STATUS_META = {
  pending_workforce: { label: 'Në pritje (Workforce)', color: '#5d55c5', background: '#f0efff', icon: '⌛' },
  approved: { label: 'Aktive', color: '#2563eb', background: '#edf4ff', icon: '↗' },
  overdue: { label: 'Afati ka skaduar', color: '#bd5b1b', background: '#fff2e8', icon: '!' },
  completed: { label: 'Përfunduar', color: '#16835e', background: '#e9f9f2', icon: '✓' },
  rejected: { label: 'Refuzuar', color: '#d23c4a', background: '#ffedef', icon: '×' }
}

const StaffingRequestCard = ({ item, hasAccessToRecruit, hasWorkforceAccess, hasAccessToApprove, hasAccessToEdit, hasAccessToEditRequested, canCompleteRequest, refreshAfterAction }) => {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const status = item.status || 'pending_workforce'
  const statusMeta = STATUS_META[status] || STATUS_META.pending_workforce
  const requested = Number.isFinite(Number(item.requested)) ? Number(item.requested) : 0
  const recruited = Number.isFinite(Number(item.recruited)) ? Number(item.recruited) : 0
  const progress = requested > 0 ? Math.min(100, Math.round((recruited / requested) * 100)) : 0
  const position = typeof item.position === 'object' ? item.position?.name : item.position
  const department = item.department?.name || '—'
  const location = item.location || '—'
  const dueDate = moment(item.due_date)
  const daysLeft = dueDate.clone().startOf('day').diff(moment().startOf('day'), 'days')
  const isClosed = ['completed', 'rejected'].includes(status)

  const openDetails = () => setDetailsOpen(true)

  return (
    <Grid item xs={12} sm={6} lg={3}>
      <Card
        variant='outlined'
        sx={{
          height: '100%',
          minHeight: 170,
          borderColor: '#dfe5ef',
          borderRadius: '10px',
          boxShadow: '0 1px 3px rgba(22, 35, 60, 0.06)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box sx={{ px: 2, pt: 1.75, pb: 1.25, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
            <Box
              component='span'
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.6,
                px: 1,
                py: 0.4,
                borderRadius: '999px',
                color: statusMeta.color,
                backgroundColor: statusMeta.background,
                fontSize: 11,
                lineHeight: 1.2,
                fontWeight: 700
              }}
            >
              <span aria-hidden='true'>{statusMeta.icon}</span>
              {statusMeta.label}
            </Box>
            <IconButton size='small' aria-label='Hap detajet' onClick={openDetails} sx={{ color: '#8ea0ba', p: 0.25 }}>
              <MoreHorizIcon fontSize='small' />
            </IconButton>
          </Box>

          <Typography sx={{ color: '#101828', fontSize: 15, lineHeight: 1.35, fontWeight: 700 }} noWrap title={position || ''}>
            {position || '—'}
          </Typography>
          <Typography sx={{ color: '#667085', fontSize: 11.5, mt: 0.25 }} noWrap title={`${department} · ${location}`}>
            {department} · {location}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.4, mb: 0.55 }}>
            <Typography sx={{ color: '#667085', fontSize: 11.5 }}>{recruited} / {requested} të rekrutuar</Typography>
            <Typography sx={{ color: '#1d2939', fontSize: 11.5, fontWeight: 700 }}>{progress}%</Typography>
          </Box>
          <LinearProgress
            variant='determinate'
            value={progress}
            sx={{
              height: 4,
              borderRadius: 4,
              backgroundColor: '#e7ecf4',
              '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundColor: '#356ae6' }
            }}
          />
        </Box>

        <Box sx={{ borderTop: '1px solid #edf0f5', px: 2, py: 1.15, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, color: '#667085' }}>
            <CalendarTodayOutlinedIcon sx={{ fontSize: 14, mr: 0.8 }} />
            <Typography sx={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
              {dueDate.isValid() ? dueDate.format('DD/MM/YYYY') : '—'}
              {!isClosed && dueDate.isValid() && (
                <Box component='span' sx={{ ml: 0.55, fontWeight: 700, color: daysLeft < 0 ? '#d92d20' : '#475467' }}>
                  · {daysLeft} ditë
                </Box>
              )}
            </Typography>
          </Box>
          <Box
            component='button'
            type='button'
            onClick={openDetails}
            sx={{
              border: 0,
              p: 0,
              ml: 1,
              background: 'transparent',
              color: '#315fdd',
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: 11.5,
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            Detajet <ChevronRightIcon sx={{ fontSize: 16, ml: 0.25 }} />
          </Box>
        </Box>
      </Card>

      <StaffingRequestDetails
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        item={item}
        canCompleteRequest={canCompleteRequest}
        hasAccessToRecruit={hasAccessToRecruit}
        hasWorkforceAccess={hasWorkforceAccess}
        hasAccessToApprove={hasAccessToApprove}
        hasAccessToEdit={hasAccessToEdit}
        hasAccessToEditRequested={hasAccessToEditRequested}
        refreshAfterAction={refreshAfterAction}
      />
    </Grid>
  )
}

export default StaffingRequestCard
